import { FC, ReactNode, useEffect, useState } from 'react'

import { cn } from '@/app/utils'
import { useSelectedProjectStore } from '@/components/entities/common/model/selected-project.store'
import { useServiceErrorsStore } from '@/components/entities/common/model/service-errors.store'
import AppProjectModal from '@/components/entities/common/ui/AppProjectModal'
import { AuthorizedContent } from '@/components/features/auth-provider/ui/AuthWrapper'
import { ServiceUnavailable } from '@/components/pages/plugs/ServiceUnavailable'
import { ErrorModal } from '@/components/shared/modal/ui/ErrorModal'
import { AppHeader } from '@/components/widgets/header/ui/Header'
import { Sidebar } from '@/components/widgets/sidebar/ui/Sidebar'

type Props = {
  children?: ReactNode
  noBottomOffset?: boolean
  className?: string
}

export const LayoutDefault: FC<Props> = ({
  children,
  noBottomOffset,
  className
}) => {
  const { isDown, isContentLarge, setIsContentLarge } = useServiceErrorsStore()
  const [isVisibleContent, setIsVisibleContent] = useState(false)
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false)
  const expandSidebar = () => setIsSidebarExpanded(true)
  const collapseSidebar = () => setIsSidebarExpanded(false)
  const toggleSidebar = () => setIsSidebarExpanded(previous => !previous)

  const {
    modalOpened,
    setModalOpened,
    modalNotClosable,
    setModalNotClosable,
    selectedProjectId
  } = useSelectedProjectStore()

  useEffect(() => {
    if (selectedProjectId) {
      setIsVisibleContent(true)
      setModalOpened(false)
      setModalNotClosable(false)
    } else {
      setModalNotClosable(true)
      setModalOpened(true)
    }
  }, [setModalOpened, setModalNotClosable, selectedProjectId])

  if (isDown) {
    return <ServiceUnavailable />
  }

  return (
    <AuthorizedContent>
      <>
        <AppProjectModal
          notClosable={modalNotClosable}
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
        />
        <ErrorModal
          headerText="Не удалось отправить"
          text="При отправке возникла ошибка, попробуйте уменьшить размер файла"
          opened={isContentLarge}
          onClose={() => setIsContentLarge(false)}
        />

        <AppHeader
          className={cn(
            'transition-all',
            'xs:left-0',
            isSidebarExpanded ? 'left-[304px] xl:left-[84px]' : 'left-[84px]'
          )}
          isSidebarExpanded={isSidebarExpanded}
          expandSidebar={expandSidebar}
        />

        <Sidebar
          isSidebarExpanded={isSidebarExpanded}
          expandSidebar={expandSidebar}
          collapseSidebar={collapseSidebar}
          toggleSidebar={toggleSidebar}
        />

        {isVisibleContent && (
          <main
            className={cn(
              'pt-80 transition-all',
              isSidebarExpanded ? 'pl-[304px] xl:pl-[84px]' : 'pl-[84px]',
              'xs:pl-0 xs:pt-62'
            )}
          >
            <div
              className={cn(
                `ml-44 mt-32`,
                'lg:ml-24 lg:mt-24',
                'xs:ml-16 xs:mt-20',
                !noBottomOffset && 'mb-60',
                !noBottomOffset && 'xs:mb-32',
                className
              )}
            >
              {children}
            </div>
          </main>
        )}
      </>
    </AuthorizedContent>
  )
}
